import { Checkbox, useMountEffect } from '@timelyapp/react';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  batchDeleteForecastSelection,
  batchUpdateForecastSelection,
} from 'routes/forecast/redux/modules/forecast.actions';
import {
  getBatchUpdateLoadingStatus,
  getForecastSelections,
} from 'routes/forecast/redux/modules/forecast.selectors';
import { batchSelectionActions } from 'routes/forecast/redux/modules/forecast.types';
import type { ThunkDispatch } from 'types/redux';

const useForecastBatchActions = (forecastIds: Array<number>) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const selections = useSelector(getForecastSelections);
  const { isBatchUpdating, isBatchDeleting } = useSelector(getBatchUpdateLoadingStatus);

  const toggleComplete = useCallback(() => {
    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'completedState',
        value: true,
      })
    );
  }, [dispatch]);

  const toggleIncomplete = useCallback(() => {
    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'completedState',
        value: false,
      })
    );
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    dispatch(batchSelectionActions.clearSelection());
  }, [dispatch]);

  const bulkUpdateSelections = useCallback(() => {
    dispatch(batchUpdateForecastSelection());
  }, [dispatch]);

  const bulkDeleteSelections = useCallback(() => {
    dispatch(batchDeleteForecastSelection());
  }, [dispatch]);

  const handleToggleBatchSelection = useCallback(() => {
    const selected = selections.forecastIds.length > 0;
    const selection = selected ? [] : forecastIds;
    dispatch(batchSelectionActions.setSelection({ selectionKey: 'forecastIds', value: selection }));
  }, [forecastIds, selections.forecastIds, dispatch]);

  const batchSelectCheckbox = useMemo(
    () => (
      <Checkbox
        checked={forecastIds.length === selections.forecastIds.length}
        indeterminate={
          selections.forecastIds.length > 0 && selections.forecastIds.length < forecastIds.length
        }
        onClick={e => e.stopPropagation()}
        onChangeChecked={handleToggleBatchSelection}
        style={{ marginRight: 16 }}
      />
    ),
    [handleToggleBatchSelection, selections.forecastIds.length, forecastIds.length]
  );

  useMountEffect(() => {
    return () => {
      clearSelection();
    };
  });

  return {
    selections,
    toggleComplete,
    toggleIncomplete,
    clearSelection,
    bulkUpdateSelections,
    bulkDeleteSelections,
    isBatchUpdating,
    isBatchDeleting,
    handleToggleBatchSelection,
    batchSelectCheckbox,
  };
};

export default useForecastBatchActions;
